import { Inject, Injectable } from '@angular/core';

import { SVG, Image } from '@svgdotjs/svg.js';
import { Point } from 'app/controls/image-cropper/region';
import { TrivialError } from 'core/error/error';
import { ImageStoreBaseInformation, ImageStorePostInformation,
         ImageStoreRequestError, ImageStoreService
       } from 'core/imagestore/imagestore.service';
import { ProductService } from 'core/product/product.service';
import { EventTrackingService } from 'core/tracking/event-tracking.service';
import { Utils } from 'core/utils';
import { ThrottledQueue } from 'core/utils/throttled-queue';
import { GalleryImage, GalleryService } from './gallery.service';
import { checkContentType, fileHash, ImageProvider, toImageStoreError,
         UploadProgressMap } from './imageprovider';

@Injectable()
export class ImageProvisioningService
{
  private static readonly MAX_PARALLEL_POSTS: number = 3;

  private _queue: ThrottledQueue<ImageStorePostInformation> =
      new ThrottledQueue(ImageProvisioningService.MAX_PARALLEL_POSTS);

  private uploadProgressMap: UploadProgressMap = {};
  private lastJobIndex: number = 0;

  constructor(
    @Inject(ImageStoreService) private imageStoreService: ImageStoreService,
    @Inject(GalleryService) private galleryService: GalleryService,
    @Inject(ProductService) private productService: ProductService,
    @Inject(EventTrackingService) private eventTrackingService:
        EventTrackingService,
  )
  { }

  public async addUserFilesToGallery(input: HTMLInputElement): Promise<void>
  {
    let uploadedImagesCount = input.files.length;
    const trackPhotoUpload = (type: string, length: number) => {
      this.eventTrackingService.track({
        category: this.productService.getCurrentProduct().id,
        action: 'gallery_photo_upload',
        label: type,
        value: length,
      });
    };

    const pending = this
      .provideLocalFiles(input.files)
      .map(p => this.galleryService.addPendingImage(p));

    trackPhotoUpload('start', input.files.length);

    await this.galleryService.beginUploadImages();

    await Promise.all(pending.map(p => p.catch(e => e))).then((results) => {
      for (let result of results) {
        if (result instanceof TrivialError) {
          this.galleryService.imageUploadFailed(result);
          uploadedImagesCount--;
        }
      }
      this.galleryService.flushFailedUploads();
    });

    await this.galleryService.finishUploadImages();
    trackPhotoUpload('finished', uploadedImagesCount);
  }

  public async addUserFileToPlaceholder(
    input: HTMLInputElement, target: Image): Promise<void>
  {
    if (input.files.length > 1) {
      this.addUserFilesToGallery(input);
    }
    else {
      const pending = this.galleryService.addPendingImage(
        this.provideLocalFile(input.files.item(0)),
        target);

      await this.galleryService.beginUploadImages();

      try {
        const result = await pending;
      } catch (error) {
        this.galleryService.imageUploadFailed(error, target);
        this.galleryService.flushFailedUploads();
      }

      await this.galleryService.finishUploadImages();
    }
  }

  /**
   * @throws ImageUploadError
   * @throws UnsupportedMediaTypeError
   * @throws ImageProvisionError
   */
  private provideLocalFile(file: File): ImageProvider
  {
    this.galleryService.imagesToUpload += 1;
    return async (id: string) =>
      await this.resolveImageStoreUpload(
        id,
        await fileHash(file),
        file.name,
        await this.tryPostFileToImageStore(file)
      );
  }

  /**
   * @throws ImageUploadError
   * @throws UnsupportedMediaTypeError
   * @throws ImageProvisionError
   */
  private provideLocalFiles(files: FileList): Array<ImageProvider>
  {
    const providers: Array<ImageProvider> = [];

    for (let i = 0; i < files.length; ++i) {
      providers.push(this.provideLocalFile(files.item(i)));
    }

    return providers;
  }

  /**
   * @throws ImageUploadError
   * @throws UnsupportedMediaTypeError
   * @throws ImageProvisionError
   */
  private async resolveImageStoreUpload(
    id: string,
    hash: string,
    cause: string,
    postInformation: ImageStoreBaseInformation): Promise<GalleryImage>
  {
    await checkContentType(postInformation.contentType, cause);

    let contentCenter = undefined;
    if (postInformation.contentCenter) {
      const splitted = postInformation.contentCenter.split(',');
      contentCenter = new Point(
          parseFloat(splitted[0].trim()), parseFloat(splitted[1].trim()));
    }

    return {
      id: id,
      url: postInformation.location,
      traudlUrls: {},
      width: postInformation.width,
      height: postInformation.height,
      hash: hash,
      expirationDate: postInformation.expirationDate,
      contentCenter: contentCenter,
    };
  }

  private updateUploadProgress(jobIndex: number, progress: number): void
  {
    this.uploadProgressMap[jobIndex] = progress;

    this.galleryService.uploadProgress = Utils
      .byValue(this.uploadProgressMap)
      .reduce((acc: number, el: number) => acc + el, 0);
  }

  private startUploadProgress(): number
  {
    const jobIndex = this.lastJobIndex++;
    this.uploadProgressMap[jobIndex] = 0;
    return jobIndex;
  }

  private finishUploadProgress(jobIndex: number): void
  {
    this.updateUploadProgress(jobIndex, 0);
    delete this.uploadProgressMap[jobIndex];
  }

  /**
   * @throws ImageUploadError
   * @throws UnsupportedMediaTypeError
   * @throws ImageProvisionError
   * @throws ImageSizeLimitExceededError
   */
  private async tryPostFileToImageStore(file: File):
    Promise<ImageStorePostInformation>
  {
    const jobIndex = this.startUploadProgress();

    try {
      const resp = await this._queue.run(
          () => this.imageStoreService.postImage(
              file,
              (p: number) => this.updateUploadProgress(jobIndex, p)));

      this.galleryService.imagesUploaded += 1;
      this.finishUploadProgress(jobIndex);
      return resp;
    } catch (error) {
      this.galleryService.imagesToUpload -= 1;
      this.finishUploadProgress(jobIndex);
      throw toImageStoreError(error as ImageStoreRequestError, file.name);
    }
  }
}
